import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import Toast from '../../molecules/Toast';
import Form from '../../molecules/Form/Form';
import Field from '../Field';
import Button from '../../molecules/Button';
import Loaders from '../Loaders';
import { StyledAcceptInvite } from './AcceptWorkspaceInvite.styles';
import teamService from '../../../services/teamService';

const AcceptWorkspaceInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { onLogin, setIsLoggedIn } = useContextHook(AuthContext, v => ({
    onLogin: v.onLogin,
    setIsLoggedIn: v.setIsLoggedIn,
  }));

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  const token = searchParams.get('token');

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      Toast({ type: 'error', message: 'Invalid invitation link' });
      navigate('/');
      return;
    }

    verifyInviteToken();
  }, [token]);

  const verifyInviteToken = async () => {
    setVerifying(true);
    try {
      const response = await teamService.verifyWorkspaceInvite(token);

      if (response.success) {
        setTokenValid(true);
        setInviteData(response.data);
        setRequiresPassword(response.data.requiresPassword || false);
        if (response.data.requiresPassword) {
          setShowPasswordForm(true);
        }
      } else {
        Toast({ type: 'error', message: response.message || 'Invalid or expired invitation' });
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to verify invitation' });
      setTimeout(() => navigate('/'), 2000);
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[@$!%*?&#])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character (@$!%*?&#)';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAcceptInvite = async e => {
    // Handle both event object and direct form data
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setAccepting(true);

    try {
      const payload = {
        token,
        password: formData.password,
        userId: inviteData?.user?.id,
        workspaceId: inviteData?.workspace?.id,
        email: inviteData?.user?.email,
        fullName: inviteData?.user?.fullName,
        finance_access: inviteData?.user?.finance_access,
      };



      const response = await teamService.acceptWorkspaceInvite(payload);


      if (response.success) {
        Toast({ type: 'success', message: 'Invitation accepted successfully! Please login to continue.' });

        setTimeout(() => {
          navigate('/', { state: { email: inviteData?.user?.email } });
        }, 1500);
      } else {
        Toast({ type: 'error', message: response.message || 'Failed to accept invitation' });
      }
    } catch (error) {
      console.error('Accept invite error:', error);
      Toast({ type: 'error', message: error.message || 'Failed to accept invitation' });
    } finally {
      setAccepting(false);
    }
  };

  const handleProceedToPassword = () => {
    setShowPasswordForm(true);
  };

  if (verifying || loading) {
    return <Loaders viewLoader />;
  }

  if (!tokenValid) {
    return (
      <StyledAcceptInvite>
        <div className="container">
          <div className="card error-card">
            <h2>Invalid Invitation</h2>
            <p>This invitation link is invalid or has expired.</p>
            <Button variant="primary" rounded onClick={() => navigate('/')}>
              Go to Login
            </Button>
          </div>
        </div>
      </StyledAcceptInvite>
    );
  }

  return (
    <StyledAcceptInvite>
      <div className="container">
        <div className="card">
          <div className="header">
            <h1>Teams Invitation</h1>
            <p className="subtitle">
              You've been invited to join <strong>{inviteData?.invitedBy?.fullName}'s</strong> team
            </p>
          </div>

          <div className="invite-details">
            <div className="detail-row">
              <span className="label">Email:</span>
              <span className="value">{inviteData?.user?.email}</span>
            </div>
            <div className="detail-row">
              <span className="label">Role:</span>
              <span className="value role">{inviteData?.role?.name?.replace('_', ' ')}</span>
            </div>
          </div>

          {showPasswordForm ? (
            <Form onSubmit={handleAcceptInvite}>
              <div className="password-section">
                <p className="password-info">Please set your password to continue</p>
                {/* <p className="password-requirements">
                  Password must be at least 8 characters and include uppercase, lowercase, number, and special character
                  (@$!%*?&#)
                </p> */}

                <Field
                  label="Create Password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  error={errors.password}
                  required
                />

                <Field
                  label="Confirm Password"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={e => handleInputChange('confirmPassword', e.target.value)}
                  error={errors.confirmPassword}
                  required
                />
              </div>

              <div className="button-group">
                <Button type="submit" variant="primary" rounded width="100%" disabled={accepting} loading={accepting}>
                  {accepting ? 'Accepting...' : 'Accept & Set Password'}
                </Button>

                <Button
                  type="button"
                  variant="light"
                  rounded
                  width="100%"
                  onClick={() => navigate('/')}
                  disabled={accepting}>
                  Cancel
                </Button>
              </div>
            </Form>
          ) : (
            <div className="button-group">
              <Button type="button" variant="primary" rounded width="100%" onClick={handleProceedToPassword}>
                Accept Invitation
              </Button>

              <Button type="button" variant="light" rounded width="100%" onClick={() => navigate('/')}>
                Decline
              </Button>
            </div>
          )}
        </div>
      </div>
    </StyledAcceptInvite>
  );
};

export default AcceptWorkspaceInvite;
